import { EnumPerfis, Perfil } from './perfil.model';
import { Servico } from './servico.model';
import { EnumSituacaoSolicitacao, Solicitacao } from './solicitacao.model';
import { UnidadeBM } from './unidadebm.model';
import { EnumSituacaoMovimentoAndamento } from './movimento-andamento.model';
import { EnumClassesAndamento } from './classe-andamento.model';

/**
 * Interface para tipagem dos serviços do usuário
 */
interface ServicoUsuario {
  SERVICO: Servico;
  CODGESTAOSERVICO?: number; // Apenas usuários gestores ou supervisores do serviço terão esse campo preenchido. Todos os gestores/supervisores têm acesso ao serviço nas mesmas unidades que sua gestão se extende.
  CODSERVICOFUNCIONARIO: number;
  UNIDADES: UnidadeBM[]; // [] = acesso a todas as unidades (sem restrição); array preenchido = unidades específicas
  RESTRICAOUNIDADESACESSO: boolean; // Será true quando CODGESTAOSERVICO não existir
}

/**
 * Interface para tipagem de responsável por recebimento de solicitação
 */
interface ResponsavelRecebimentoSolicitacao {
  CODCATEGORIASOLICITACAO: number;
  TITULO: string;
  UNIDADES: UnidadeBM[];
}

interface CategoriaRecursoComissaoTecnica {
  CODCATEGORIARECURSO: number;
  TITULO: string;
}

interface TipoRecursoComissaoTecnica {
  CODTIPORECURSO: number;
  TITULO: string;
}

interface ComissaoTecnicaUsuario {
  CODCOMISSAOTECNICA: number;
  TITULO: string;
  SIGLA: string | null;
  CODESPECIALISTA: number;
  FUNCAO: 'PRESIDENTE' | 'VICE-PRESIDENTE' | 'MEMBRO';
  CATEGORIASRECURSO: CategoriaRecursoComissaoTecnica[];
  TIPOSRECURSO: TipoRecursoComissaoTecnica[];
}

/**
 * Classe Usuario com métodos de verificação de perfil, permissões e unidades de atuação.
 * O AuthService retorna uma instância desta classe através do método usuarioLogado() quando a sessão é válida.
 */
export class Usuario {
  // ========== PROPRIEDADES (herdadas de Funcionario, exceto CODUNIDADE e UNIDADE) ==========
  CODFUNC: number;
  NOME: string;
  MATRICULA?: string;
  PGQUADRO?: string;
  NOMEGUERRA?: string;

  // ========== PROPRIEDADES ESPECÍFICAS DO USUARIO ==========
  CODUSUARIO: number;
  CPF: string;
  TELEFONE: string;
  EMAIL: string;
  LOTACAO: UnidadeBM[]; // Pode ser uma ou várias unidades
  UNIDADESELECIONADA: UnidadeBM; // Unidade selecionada pelo usuário com mais de uma lotação (disponível apenas no Frontend)
  PERFIL: Perfil;
  FOTO?: string; // URL da foto do usuário
  SERVICOS?: ServicoUsuario[]; // Serviços aos quais o usuário tem acesso
  RESPONSAVELRECEBIMENTOSOLICITACAO?: ResponsavelRecebimentoSolicitacao[]; // Unidades onde o usuário é responsável pelo recebimento de solicitações de alguma categoria de solicitação
  COMISSOESTECNICAS?: ComissaoTecnicaUsuario[];

  // ========== CONSTRUCTOR ==========
  constructor(data: Partial<Usuario>) {
    Object.assign(this, data);
  }

  // ========== MÉTODOS DE PERFIL ==========

  /**
   * Verifica se o usuário possui perfil de Administrador
   */
  isAdministrador(): boolean {
    return this.PERFIL?.CODPERFIL === EnumPerfis.ADMINISTRADOR;
  }

  /**
   * Verifica se o usuário possui perfil de Supervisor
   */
  isSupervisor(): boolean {
    return this.PERFIL?.CODPERFIL === EnumPerfis.SUPERVISOR;
  }

  /**
   * Verifica se o usuário possui perfil Geral
   */
  isPerfilGeral(): boolean {
    return this.PERFIL?.CODPERFIL === EnumPerfis.GERAL;
  }

  /**
   * Retorna o código do perfil do usuário
   */
  getCodigoPerfil(): number | null {
    return this.PERFIL?.CODPERFIL ?? null;
  }

  // ========== MÉTODOS DE ESPECIALISTA ==========

  /**
   * Verifica se o usuário participa de alguma comissão técnica.
   */
  isEspecialista(): boolean {
    return (this.COMISSOESTECNICAS ?? []).length > 0;
  }

  /**
   * Verifica se o usuário participa de uma comissão técnica específica.
   */
  isEspecialistaComissaoTecnica(codComissaoTecnica: number): boolean {
    return this.COMISSOESTECNICAS?.some(comissao => comissao.CODCOMISSAOTECNICA === codComissaoTecnica) ?? false;
  }

  /**
   * Verifica se o usuário participa de comissão técnica vinculada diretamente a uma categoria de recurso.
   */
  isEspecialistaCategoriaRecurso(codCategoriaRecurso: number): boolean {
    return this.COMISSOESTECNICAS?.some(comissao =>
      comissao.CATEGORIASRECURSO?.some(categoria => categoria.CODCATEGORIARECURSO === codCategoriaRecurso)
    ) ?? false;
  }

  /**
   * Verifica se o usuário participa de comissão técnica vinculada diretamente a um tipo de recurso.
   */
  isEspecialistaTipoRecurso(codTipoRecurso: number): boolean {
    return this.COMISSOESTECNICAS?.some(comissao =>
      comissao.TIPOSRECURSO?.some(tipo => tipo.CODTIPORECURSO === codTipoRecurso)
    ) ?? false;
  }

  // ========== MÉTODOS DE SERVIÇO ==========

  /**
   * Busca um serviço específico pelo path
   */
  private getServico(servicePath: string): ServicoUsuario | undefined {
    return this.SERVICOS?.find(s => s.SERVICO.PATH === servicePath);
  }

  /**
   * Verifica se o usuário tem acesso a um serviço (qualquer nível)
   */
  temAcessoServico(servicePath: string): boolean {
    return !!this.getServico(servicePath);
  }

  /**
   * Verifica se o usuário é supervisor de um serviço específico.
   * Retorna true se tem CODGESTAOSERVICO preenchido E possui perfil de Supervisor.
   */
  isSupervisorServico(servicePath: string): boolean {
    const servico = this.getServico(servicePath);
    return !!servico?.CODGESTAOSERVICO && this.isSupervisor();
  }

  /**
   * Retorna as unidades onde o usuário tem supervisão de um serviço.
   * Retorna array vazio se não for supervisor do serviço.
   */
  getUnidadesSupervisaoServico(servicePath: string): UnidadeBM[] {
    if (!this.isSupervisorServico(servicePath)) return [];
    return this.getServico(servicePath)?.UNIDADES ?? [];
  }

  /**
   * Retorna as unidades onde o usuário tem acesso a um serviço (funcionário comum).
   * Retorna array vazio se não tiver acesso ao serviço.
   */
  getUnidadesAcessoServico(servicePath: string): UnidadeBM[] {
    return this.getServico(servicePath)?.UNIDADES ?? [];
  }

  // ========== MÉTODOS DE SOLICITAÇÃO ==========

  /**
   * Retorna as unidades onde o usuário é responsável pelo recebimento de solicitações
   */
  getUnidadesResponsavelRecebimento(): UnidadeBM[] {
    const unidades = this.RESPONSAVELRECEBIMENTOSOLICITACAO?.flatMap(r => r.UNIDADES) ?? [];
    // Remove duplicatas baseado no CODUNIDADE
    return unidades.filter((unidade, index, self) => self.findIndex(u => u.CODUNIDADE === unidade.CODUNIDADE) === index);
  }

  /**
   * Verifica se o usuário é responsável pelo recebimento de solicitações em uma unidade específica
   */
  isResponsavelRecebimentoUnidade(codUnidade: number): boolean {
    return this.getUnidadesResponsavelRecebimento().some(u => u.CODUNIDADE === codUnidade);
  }

  /**
   * Verifica se o usuário é responsável pelo recebimento de uma categoria específica em uma unidade específica
   */
  isResponsavelRecebimentoCategoriaUnidade(codCategoria: number, codUnidade: number): boolean {
    const responsavel = this.RESPONSAVELRECEBIMENTOSOLICITACAO?.find(r => r.CODCATEGORIASOLICITACAO === codCategoria);
    return responsavel?.UNIDADES?.some(u => u.CODUNIDADE === codUnidade) ?? false;
  }

  /**
   * Verifica se uma unidade está em uma lista de unidades.
   * Lista vazia significa acesso irrestrito (todas as unidades).
   */
  private unidadeEstaEm(codUnidade: number, unidades: UnidadeBM[]): boolean {
    if (unidades.length === 0) return true;
    return unidades.some(u => u.CODUNIDADE === codUnidade);
  }

  private isLotadoNaUnidade(codUnidade: number): boolean {
    return this.LOTACAO?.some(u => u.CODUNIDADE === codUnidade) ?? false;
  }

  ehSolicitanteSolicitacao(solicitacao: Solicitacao): boolean {
    const cpfUsuario = String(this.CPF ?? '').replace(/\D/g, '');
    const cpfSolicitante = String(solicitacao?.SOLICITANTE?.CPF ?? '').replace(/\D/g, '');
    if (cpfUsuario && cpfSolicitante) return cpfUsuario === cpfSolicitante;
    return this.CODFUNC > 0 && solicitacao?.SOLICITANTE?.CODFUNC === this.CODFUNC;
  }

  possuiPosseEfetivaSolicitacao(solicitacao: Solicitacao): boolean {
    return this.CODFUNC > 0
      && solicitacao?.FUNCIONARIORESPONSAVEL?.CODFUNC === this.CODFUNC
      && solicitacao?.ULTIMOMOVIMENTO?.SITUACAO?.CODSITUACAO === EnumSituacaoMovimentoAndamento.RECEBIDO;
  }

  /**
   * Verifica se o usuário pode RECEBER uma solicitação.
   *
   * Regras:
   * - Solicitação deve estar "Em Andamento"
   * - Último movimento deve estar "Aguardando"
   * - Solicitação deve estar na unidade de lotação OU em unidade onde é responsável pelo recebimento da categoria
   *
   * Nota: Não verifica se já é responsável, pois ao transferir/enviar o destinatário é setado como responsável,
   * mas qualquer pessoa da unidade pode receber e se tornar o novo responsável.
   */
  podeReceberSolicitacao(solicitacao: Solicitacao): boolean {
    // 1. Verifica situação Em Andamento
    if (solicitacao?.SITUACAO?.CODSITUACAO !== EnumSituacaoSolicitacao.EM_ANDAMENTO) {
      return false;
    }

    // 2. Verifica se último movimento está aguardando
    const ultimoMovimentoAguardando = solicitacao?.ULTIMOMOVIMENTO?.SITUACAO?.CODSITUACAO === EnumSituacaoMovimentoAndamento.AGUARDANDO;
    if (!ultimoMovimentoAguardando) {
      return false;
    }

    if (this.ehSolicitanteSolicitacao(solicitacao) && solicitacao.ULTIMOANDAMENTO?.CLASSE?.PATH === EnumClassesAndamento.ENVIO) {
      return false;
    }

    // 3. Remetente do movimento não pode receber/recusar (evita loop intra-unidade "de mim para mim")
    const responsavelMovimento = solicitacao?.ULTIMOMOVIMENTO?.RESPONSAVELMOVIMENTO;
    if (responsavelMovimento?.CODFUNC === this.CODFUNC) {
      return false;
    }

    const unidadeAtual = solicitacao?.UNIDADEATUAL?.CODUNIDADE;
    const codCategoria = solicitacao?.CATEGORIASOLICITACAO?.CODCATEGORIASOLICITACAO;

    if (!unidadeAtual || !codCategoria) return false;

    // 4. Verifica se está na unidade de lotação
    if (this.isLotadoNaUnidade(unidadeAtual)) {
      return true;
    }

    // 5. Verifica se é responsável pelo recebimento da categoria na unidade atual
    return this.isResponsavelRecebimentoCategoriaUnidade(codCategoria, unidadeAtual);
  }

  /**
   * Verifica se o usuário pode ASSUMIR uma solicitação.
   *
   * Regras:
   * - Solicitação deve estar "Em Andamento"
   * - Usuário não pode assumir uma solicitação que já está sob sua responsabilidade
   * - Remetente do último movimento aguardando não pode assumir, inclusive Administrador
   * - Se pode RECEBER a solicitação (último movimento aguardando), não pode assumir
   * - ADMINISTRADOR: pode assumir qualquer solicitação
   * - SUPERVISOR rsol: unidade de lotação + unidades de supervisão + unidades responsável recebimento da categoria
   * - GERAL: unidade de lotação + unidades responsável recebimento da categoria
   * - Primeiro envio: lotados na unidade histórica ou responsáveis pela categoria nela
   */
  podeAssumirSolicitacao(solicitacao: Solicitacao): boolean {
    // 1. Verifica situação Em Andamento
    if (solicitacao?.SITUACAO?.CODSITUACAO !== EnumSituacaoSolicitacao.EM_ANDAMENTO) {
      return false;
    }

    // 2. Verifica se já é responsável pela solicitação
    if (solicitacao?.FUNCIONARIORESPONSAVEL?.CODFUNC === this.CODFUNC) {
      return false;
    }

    if (solicitacao?.ULTIMOMOVIMENTO?.SITUACAO?.CODSITUACAO === EnumSituacaoMovimentoAndamento.AGUARDANDO
      && solicitacao.ULTIMOMOVIMENTO.RESPONSAVELMOVIMENTO?.CODFUNC === this.CODFUNC) {
      return false;
    }

    // 3. Se pode RECEBER a solicitação, não pode assumir
    if (this.podeReceberSolicitacao(solicitacao)) {
      return false;
    }

    const unidadeAtual = solicitacao?.UNIDADEATUAL?.CODUNIDADE;
    const codCategoria = solicitacao?.CATEGORIASOLICITACAO?.CODCATEGORIASOLICITACAO;
    if (!unidadeAtual || !codCategoria) return false;

    // 4. Administrador: pode assumir qualquer solicitação
    if (this.isAdministrador()) {
      return true;
    }

    // 5. Verifica se está na unidade de lotação
    const unidadeLotacao = this.UNIDADESELECIONADA?.CODUNIDADE;
    if (unidadeAtual === unidadeLotacao) {
      return true;
    }

    // 6. Verifica se é responsável pelo recebimento da categoria na unidade atual
    if (this.isResponsavelRecebimentoCategoriaUnidade(codCategoria, unidadeAtual)) {
      return true;
    }

    // 7. Primeiro envio: unidade historicamente destinatária, sem alterar o destino da assunção
    const unidadePrimeiroEnvio = solicitacao?.UNIDADEDESTINOPRIMEIROENVIO?.CODUNIDADE;
    if (unidadePrimeiroEnvio && (this.isLotadoNaUnidade(unidadePrimeiroEnvio)
      || this.isResponsavelRecebimentoCategoriaUnidade(codCategoria, unidadePrimeiroEnvio))) {
      return true;
    }

    // 8. Lógica de supervisão
    if (this.isSupervisorServico('rsol')) {
      const unidadesSupervisao = this.getUnidadesSupervisaoServico('rsol');
      return this.unidadeEstaEm(unidadeAtual, unidadesSupervisao);
    }

    return false;
  }

  /**
   * Verifica se o usuário pode REQUISITAR uma solicitação.
   *
   * Regras:
   * - Administrador NUNCA requisita (já pode assumir tudo)
   * - Solicitação deve estar "Em Andamento"
   * - Usuário não pode requisitar uma solicitação que já está sob sua responsabilidade
   * - Se pode RECEBER a solicitação (último movimento aguardando), não pode requisitar
   * - Pode requisitar solicitações em unidades fora das que pode assumir
   */
  podeRequisitarSolicitacao(solicitacao: Solicitacao): boolean {
    // Administrador nunca requisita
    if (this.isAdministrador()) {
      return false;
    }
    // Verifica situação Em Andamento
    if (solicitacao?.SITUACAO?.CODSITUACAO !== EnumSituacaoSolicitacao.EM_ANDAMENTO) {
      return false;
    }
    // Verifica se já é responsável pela solicitação
    if (solicitacao?.FUNCIONARIORESPONSAVEL?.CODFUNC === this.CODFUNC) {
      return false;
    }

    // Se pode RECEBER a solicitação, não pode requisitar
    if (this.podeReceberSolicitacao(solicitacao)) {
      return false;
    }

    // Se o último andamento corresponde a uma requisição, ninguém pode requisitar.
    const ultimoAndamentoPath = solicitacao?.ULTIMOANDAMENTO?.CLASSE?.PATH;
    if (ultimoAndamentoPath === EnumClassesAndamento.SOLICITACAO_REQUISITADA) {
      return false;
    }

    // Pode requisitar se NÃO pode assumir
    return !this.podeAssumirSolicitacao(solicitacao);
  }

  /**
   * Verifica se o usuário pode DECIDIR (aprovar ou reprovar) uma requisição pendente.
   *
   * Regras (espelham o papel de decisor do WS):
   * - Solicitação "Em Andamento" cujo último andamento é a requisição
   * - Movimento "Aguardando": decide quem pode receber a solicitação
   * - Movimento "Recebido": decide somente o responsável com posse
   */
  podeDecidirRequisicaoSolicitacao(solicitacao: Solicitacao): boolean {
    if (solicitacao?.SITUACAO?.CODSITUACAO !== EnumSituacaoSolicitacao.EM_ANDAMENTO) return false;
    if (solicitacao?.ULTIMOANDAMENTO?.CLASSE?.PATH !== EnumClassesAndamento.SOLICITACAO_REQUISITADA) return false;

    const situacaoMovimento = solicitacao?.ULTIMOMOVIMENTO?.SITUACAO?.CODSITUACAO;
    if (situacaoMovimento === EnumSituacaoMovimentoAndamento.AGUARDANDO) return this.podeReceberSolicitacao(solicitacao);
    return this.possuiPosseEfetivaSolicitacao(solicitacao);
  }
}
